// src/components/CardContainer.jsx
import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

/**
 * A generic card container component that handles consistent styling and expandable behavior.
 * Uses Professional Slate color scheme.
 * Mobile-responsive with appropriate paddings and touch targets.
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Card content
 * @param {React.ReactNode} props.header - Card header content
 * @param {React.ReactNode} props.footer - Card footer content (optional)
 * @param {boolean} props.defaultExpanded - Whether the card is expanded by default
 * @param {boolean} props.expandable - Whether the card can be expanded/collapsed
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.embedded - Whether this card is embedded in another card
 * @param {boolean} props.fullWidth - Whether the card should take full width on all devices
 */
const CardContainer = ({ 
  children, 
  header,
  footer,
  defaultExpanded = true,
  expandable = false,
  className = "",
  embedded = false,
  fullWidth = false,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const toggleExpand = () => {
    setExpanded(!expanded);
  };

  // Professional Slate color scheme
  const colors = {
    topGradient: "bg-gradient-to-r from-slate-400 to-gray-500",
    badge: "bg-slate-200 text-slate-700",
    checkboxBg: "bg-slate-200",
    checkboxText: "text-slate-700",
    buttonText: "text-slate-700"
  };

  // For detail pages, adjust to fullWidth on mobile 
  // Check if we're on any of the detail pages by matching paths like /q/123, /a/123, etc.
  // Also match slug pages like /o/my-opinion-123
  const isDetailPage = window.location.pathname.match(/\/[qpoa]\/\d+/) || 
                       window.location.pathname.match(/\/[qpoa]\/[a-z-]+-\d+/) ||
                       window.location.pathname.match(/\/quotes\/\d+/) ||
                       window.location.pathname.match(/\/articles\/\d+/) ||
                       window.location.pathname.match(/\/policies\/\d+/) ||
                       window.location.pathname.match(/\/opinions\/\d+/);
  
  // Always use full width regardless of embedding
  const finalContainerStyles = "w-full mx-auto bg-white rounded-md sm:rounded-xl shadow-sm sm:shadow-md overflow-hidden border border-gray-200 mb-4 sm:mb-8 card-container";
  
  return (
    <div className={`mobile-card ${finalContainerStyles} ${className} ${isDetailPage ? 'mobile-detail-view-card' : ''}`}>
      {/* Top gradient border accent - only show on detail pages (URLs with an ID), not list pages */}
      {!embedded && window.location.pathname.match(/\/q\/\d+/) && (
        <div className={`h-1 ${colors.topGradient}`}></div>
      )}
      
      {/* Card Header - mobile optimized padding */}
      {header && (
        <div className="px-4 sm:px-6 pt-4 sm:pt-6 pb-3 sm:pb-4 flex justify-between items-center">
          <div className="flex-grow">
            {header}
          </div>
          
          {expandable && (
            <button
              onClick={toggleExpand}
              className={`flex items-center ${colors.buttonText} bg-slate-100 hover:bg-slate-200 px-3 py-2 rounded-md text-sm mobile-touch-target`}
              aria-expanded={expanded}
              aria-label={expanded ? "Collapse content" : "Expand content"}
            >
              {expanded ? (
                <>
                  <ChevronUp size={16} className="mr-1" />
                  <span className="hidden sm:inline">Collapse</span>
                </>
              ) : (
                <>
                  <ChevronDown size={16} className="mr-1" />
                  <span className="hidden sm:inline">Expand</span>
                </>
              )}
            </button>
          )}
        </div>
      )}

      {/* Visual divider if header exists - consistent across screen sizes */}
      {header && (
        <div className="px-4 sm:px-6">
          <div className="h-px bg-gray-200"></div>
        </div>
      )}

      {/* Card Content */}
      {(!expandable || expanded) && (
        <div className="bg-white w-full">
          {children}
        </div>
      )}
      
      {/* Card Footer - mobile optimized padding */}
      {footer && (
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-100 bg-white">
          {footer}
        </div>
      )}
    </div>
  );
};

export default CardContainer;